import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cardBorderClassName } from "../../lib/cardStyles";
import { DEMO_BOOKING_URL } from "../../lib/pricing";

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left text-white font-semibold text-sm hover:bg-white/5 transition-colors"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        <span>{question}</span>
        <span className="ml-4 text-purple-400 text-lg">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div
          className="px-6 pb-4 text-sm text-gray-400 leading-relaxed"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {answer}
        </div>
      )}
    </div>
  );
}

const FAQ_ITEMS = [
  {
    q: "Can it transfer to a real person?",
    a: "Yes. You set the trigger phrases — 'speak to a human', 'billing question', anything you choose — and calls route instantly to whichever number you specify. You're always in control of who gets what call.",
  },
  {
    q: "How long does setup take?",
    a: "Most businesses are live in under an hour. Set your greeting, add your business knowledge, and your AI starts answering calls. No technical experience required.",
  },
  {
    q: "What if the AI can't answer something?",
    a: "It tells the caller someone will follow up, logs the call, and flags it in your dashboard. Nothing falls through the cracks — you see every unanswered question.",
  },
  {
    q: "Does it remember repeat callers?",
    a: "Yes. Every caller gets a profile. When they call back, your AI already knows who they are and picks up where the last call left off.",
  },
  {
    q: "Is there a contract?",
    a: "No. NeuroSphere is month to month with no setup fees. Plans start at $299 a month and our team sets yours up with you.",
  },
  {
    q: "Will it sound robotic?",
    a: "No. NeuroSphere uses ElevenLabs voice technology — the same used by major podcasts and audiobooks. Most callers can't tell it's AI. You can hear a demo by calling our demo line.",
  },
  {
    q: "Can I change what it says?",
    a: "Yes, completely. You control your AI's name, greeting, personality, and knowledge base from your dashboard. Changes go live instantly.",
  },
  {
    q: "Does it work with my existing phone number?",
    a: "Yes. We port your existing number or assign you a new one through Twilio. Either way, calls route through NeuroSphere automatically.",
  },
  {
    q: "What if I'm a solo practitioner?",
    a: "NeuroSphere is perfect for solo businesses. Our Starter plan is built for single-seat operations — one AI, one number, all your calls handled.",
  },
  {
    q: "What happens during setup?",
    a: "Setup takes about 5 minutes. You tell us your business name, industry, and how you want calls handled. Your AI is live the same day — no tech team, no long onboarding call.",
  },
];

export default function MarketingHome() {
  const [scrolled, setScrolled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.title = "NeuroSphere — AI Phone Receptionist for Small Businesses | Answers Every Call 24/7";
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const elements = document.querySelectorAll("[data-animate]");
    const reveal = (el: Element) => el.classList.add("visible");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => observer.observe(el));

    const fallback = window.setTimeout(() => {
      elements.forEach((el) => {
        if (!el.classList.contains("visible")) reveal(el);
      });
    }, 1000);

    return () => {
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, []);

  const navLinks = (
    <>
      <Link
        to="/pricing"
        onClick={() => setSidebarOpen(false)}
        className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/30 hover:text-white"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        Pricing
      </Link>
      <Link
        to="/login"
        onClick={() => setSidebarOpen(false)}
        className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/30 hover:text-white"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        Login
      </Link>
      <a
        href={DEMO_BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setSidebarOpen(false)}
        className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#0a0a12] hover:bg-white/90"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        Book a demo
      </a>
    </>
  );

  return (
    <div className="min-h-screen w-full bg-transparent text-white relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 hex-grid opacity-30"></div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed right-0 top-0 z-50 h-full w-64 bg-[#0d0d12] border-l border-white/10 p-6 flex flex-col gap-4 transition-transform duration-300 md:hidden ${
          sidebarOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          aria-label="Close menu"
        >
          <i className="ri-close-line text-2xl" />
        </button>
        <div className="mt-10 flex flex-col gap-3">{navLinks}</div>
      </aside>

      {/* Nav Bar */}
      <header
        className="relative z-20 border-b border-white/10 sticky top-0 transition-all duration-300"
        style={{
          background: scrolled ? "rgba(10, 10, 18, 0.85)" : "transparent",
          backdropFilter: scrolled ? "blur(12px)" : "none",
        }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <div
              className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-orange-400"
              role="img"
              aria-label="NeuroSphere"
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-wide" style={{ fontFamily: "Space Grotesk, sans-serif" }}>NeuroSphere</div>
              <div className="text-xs text-white/60" style={{ fontFamily: "Space Grotesk, sans-serif" }}>AI Receptionist for Small Business</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-3">{navLinks}</div>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="md:hidden text-gray-400 hover:text-white transition-colors"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-2xl" />
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative z-10 flex flex-col items-center px-6 text-center">
        <div className="relative overflow-hidden w-full flex flex-col items-center pt-32 pb-8">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-10 blur-2xl animate-pulse"
              style={{ background: "radial-gradient(circle, #8a2be2 0%, #ff6a00 100%)" }}
            />
          </div>

          {/* Main Headline */}
          <h1
            className="relative text-5xl md:text-7xl font-extrabold bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent mb-6"
            style={{
              fontFamily: "Orbitron, sans-serif",
              textShadow:
                "0 0 40px rgba(138, 43, 226, 0.3), 0 0 80px rgba(255, 106, 0, 0.2)",
            }}
          >
            Never miss another call.
          </h1>

          {/* Subheadline */}
          <p
            className="relative text-xl md:text-2xl text-gray-300 max-w-3xl mb-12 tracking-wide"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            NeuroSphere is the AI receptionist that answers 24/7, takes messages, transfers urgent calls, and remembers every caller.
          </p>

          {/* CTA Buttons */}
          <div className="relative flex flex-col sm:flex-row gap-4 mt-6">
            <a
              href={DEMO_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-10 py-4 rounded-lg bg-gradient-to-r from-purple-500 to-orange-400 hover:from-purple-600 hover:to-orange-500 transition-all duration-300 text-white font-semibold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 whitespace-nowrap cursor-pointer text-center"
              style={{ fontFamily: "Space Grotesk, sans-serif" }}
            >
              Book a demo
            </a>

            <Link
              to="/login"
              className="px-10 py-4 rounded-lg border-2 border-purple-400/60 hover:bg-purple-600/30 hover:border-purple-400 transition-all duration-300 font-semibold backdrop-blur-sm whitespace-nowrap cursor-pointer text-center"
              style={{ fontFamily: "Space Grotesk, sans-serif" }}
            >
              Login
            </Link>
          </div>
          <Link
            to="/pricing"
            className="relative mt-4 text-sm text-gray-400 underline hover:text-gray-300"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            View pricing →
          </Link>

          {/* Tagline badge */}
          <div
            className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mt-2"
            style={{
              background: "rgba(138,43,226,0.15)",
              border: "1px solid rgba(138,43,226,0.3)",
              color: "rgba(255,255,255,0.85)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            <i className="ri-flashlight-line text-purple-400" />
            Live in a day. No sales call required.
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-32 max-w-6xl w-full">
          {[
            {
              icon: "ri-phone-line",
              title: "Answers every call, 24/7",
              desc: "Day, night, weekends, lunch rush. Real conversations, not phone trees.",
              to: "/features/answers-every-call",
            },
            {
              icon: "ri-brain-line",
              title: "Remembers every caller",
              desc: "Greets returning callers like a longtime employee and picks up where the last call left off.",
              to: "/features/caller-memory",
            },
            {
              icon: "ri-links-line",
              title: "Answers, takes messages, and transfers",
              desc: "Answers your FAQs, takes a message when you're busy, and hands urgent calls to a real person instantly.",
              to: "/features/books-answers-transfers",
            },
          ].map((feature, idx) => (
            <Link
              key={idx}
              to={feature.to}
              data-animate
              className={`relative p-8 rounded-2xl bg-[#1a1a24]/60 backdrop-blur-sm transition-all duration-300 group block cursor-pointer ${cardBorderClassName}`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-orange-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 flex items-center justify-center mb-6 mx-auto">
                  <i
                    className={`${feature.icon} text-5xl bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent`}
                  ></i>
                </div>

                <h3
                  className="text-xl font-bold mb-3 bg-gradient-to-r from-purple-300 to-orange-300 bg-clip-text text-transparent"
                  style={{ fontFamily: "Orbitron, sans-serif" }}
                >
                  {feature.title}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* How it works */}
        <section className="mt-32 max-w-6xl w-full px-6">
          <p
            className="text-xs uppercase tracking-widest text-purple-400 text-center mb-3"
            data-animate
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            How it works
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold text-white text-center mb-4"
            data-animate
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            Up and running in under a day
          </h2>
          <p
            className="text-gray-400 text-center max-w-2xl mx-auto"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            No tech team required. No long contracts. Just a smarter phone system.
          </p>

          <div className="relative mt-16">
            <div
              className="hidden md:block absolute top-8 left-[18%] right-[18%] h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(138, 43, 226, 0.35), rgba(255, 106, 0, 0.35), transparent)",
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {[
                {
                  step: 1,
                  title: "Set up your AI",
                  desc: "Tell us your business name, industry, and how you want calls handled. Takes about 5 minutes.",
                },
                {
                  step: 2,
                  title: "Your AI answers every call",
                  desc: "Your AI receptionist picks up 24/7, greets callers by name, answers questions, and transfers when needed.",
                },
                {
                  step: 3,
                  title: "Review and improve",
                  desc: "See every call summarized in your dashboard. Edit your AI's knowledge base anytime.",
                },
              ].map(({ step, title, desc }) => (
                <div key={step} className="text-center relative z-10">
                  <div
                    data-animate
                    className="text-8xl font-black mb-4"
                    style={{
                      fontFamily: "Orbitron, sans-serif",
                      background: "linear-gradient(135deg, #8a2be2 0%, #ff6a00 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {step}
                  </div>
                  <h3
                    className="text-white font-semibold mb-2"
                    style={{ fontFamily: "Space Grotesk, sans-serif" }}
                  >
                    {title}
                  </h3>
                  <p
                    className="text-gray-400 text-sm leading-relaxed"
                    style={{ fontFamily: "Space Grotesk, sans-serif" }}
                  >
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What we do */}
        <section className="mt-32 max-w-6xl w-full px-6">
          <p
            className="text-xs uppercase tracking-widest text-purple-400 text-center mb-3"
            data-animate
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            Built for your business
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
            data-animate
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            Everything your front desk does, now on autopilot
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: "ri-phone-line",
                title: "24/7 Call Answering",
                desc: "Never miss a call again. Your AI answers day, night, weekends, and holidays with a natural, human voice.",
              },
              {
                icon: "ri-brain-line",
                title: "Caller Memory",
                desc: "Returning callers are greeted by name. Your AI remembers their history, preferences, and past conversations.",
              },
              {
                icon: "ri-phone-fill",
                title: "Smart Transfer",
                desc: "Urgent calls go straight to the right person. Screened transfers let your team decide before picking up.",
              },
              {
                icon: "ri-file-list-line",
                title: "Call Summaries",
                desc: "Every call is automatically summarized and logged. Review what your AI handled and how callers were helped.",
              },
              {
                icon: "ri-book-open-line",
                title: "Knowledge Base",
                desc: "Train your AI with your FAQs, services, and policies. The more it knows, the better it handles calls.",
              },
              {
                icon: "ri-building-line",
                title: "Works for your industry",
                desc: "From medical offices to law firms to contractors — if your business gets phone calls, NeuroSphere handles them.",
              },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                data-animate
                className={`p-6 rounded-xl transition-all duration-300 hover:-translate-y-1 ${cardBorderClassName}`}
                style={{
                  background: "rgba(28, 28, 37, 0.7)",
                  fontFamily: "Space Grotesk, sans-serif",
                }}
              >
                <i className={`${icon} text-2xl text-purple-400 mb-3 block`} />
                <h3 className="text-white font-semibold mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <div className="relative z-20 mt-24 max-w-3xl mx-auto px-6">
          <h2
            className="text-2xl font-bold text-white text-center mb-10"
            data-animate
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Common questions
          </h2>
          <div className="space-y-4">
            {FAQ_ITEMS.map(({ q, a }) => (
              <FaqItem key={q} question={q} answer={a} />
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-gray-800 py-8 text-center">
        <div className="flex justify-center gap-6 mb-4 text-sm text-gray-500">
          <Link to="/">Home</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/login">Login</Link>
          <a href={DEMO_BOOKING_URL} target="_blank" rel="noopener noreferrer">Book a demo</a>
        </div>
        <p className="text-gray-500 text-sm mb-3" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
          © 2026 NeuroSphere. All rights reserved.
        </p>
        <div className="flex justify-center gap-6">
          <a href="#" className="text-gray-500 text-sm hover:text-gray-400" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Privacy Policy</a>
          <a href="#" className="text-gray-500 text-sm hover:text-gray-400" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Terms of Service</a>
        </div>
      </footer>

      <style>{`
        .hex-grid {
          background-image: 
            linear-gradient(30deg, rgba(138, 43, 226, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(138, 43, 226, 0.05) 87.5%, rgba(138, 43, 226, 0.05)),
            linear-gradient(150deg, rgba(138, 43, 226, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(138, 43, 226, 0.05) 87.5%, rgba(138, 43, 226, 0.05)),
            linear-gradient(30deg, rgba(138, 43, 226, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(138, 43, 226, 0.05) 87.5%, rgba(138, 43, 226, 0.05)),
            linear-gradient(150deg, rgba(138, 43, 226, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(138, 43, 226, 0.05) 87.5%, rgba(138, 43, 226, 0.05)),
            linear-gradient(60deg, rgba(255, 106, 0, 0.03) 25%, transparent 25.5%, transparent 75%, rgba(255, 106, 0, 0.03) 75%, rgba(255, 106, 0, 0.03)),
            linear-gradient(60deg, rgba(255, 106, 0, 0.03) 25%, transparent 25.5%, transparent 75%, rgba(255, 106, 0, 0.03) 75%, rgba(255, 106, 0, 0.03));
          background-size: 80px 140px;
          background-position: 0 0, 0 0, 40px 70px, 40px 70px, 0 0, 40px 70px;
        }
      `}</style>
    </div>
  );
}
